import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import {
  TbaTransaction,
  ControlledParameter,
  Currency,
  Message,
  TbaTransactionsModalPayload,
  Tba,
  TbaParameter,
  TbaTransactionsPayload,
  Transaction } from '../../../shared/models';
import { PaginationConstants,
  // TranslationConstants
} from '../../../shared/constants';
import {
  TbaEvaluationMethodEnum,
  TbaParameterAmountEnum,
  TransactionTypeEnum } from '../../../shared/models/enums';

import { ModalService, ClosableModal } from '../../../shared/modules/modal';
import {
  SessionService,
  NotificationService,
  TbasService,
  UtilsService,
  NumberFormatService
} from '../../../shared/services';
import { TbaConstants } from '../../../shared/constants/tba';

import { ConfirmationModalComponent } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { MessageModalComponent } from '../../../shared/components/message/message-modal.component';

@Component({
  selector: 'add-controlled-transactions-modal',
  templateUrl: './add-controlled-transactions-modal.component.html',
})
export class AddControlledTransactionsModalComponent extends ClosableModal implements OnInit, OnDestroy {
  private subscriptions = [];

  data: TbaTransactionsModalPayload;
  transactions: TbaTransaction[] = [];
  parameters: ControlledParameter[];
  upeCurrency: Currency;
  upeCurrencyScale: number;
  parametersChanged = false;

  // needed for pagination
  pendingSelectedTransactions: TbaTransaction[] = [];
  pendingTransactionsToRemove: TbaTransaction[] = [];

  // backup for formatted input fields
  inputOldValue = {
    parameterValue: '',
  };

  pageSizes: number[];

  currentPage = 1;
  totalItems: number;
  selectedPageSize = PaginationConstants.DEFAULT_PAGE_SIZE;

  errors: string[] = [];

  constructor(protected modalService: ModalService,
    private _tbasService: TbasService,
    private _sessionService: SessionService,
    private _utilsService: UtilsService,
    private _notificationsService: NotificationService,
    private _numberFormatService: NumberFormatService
  ) {
    super(modalService);
  }

  ngOnInit(): void {
    this.upeCurrency = this._sessionService.getContainerUpeCurrency();
    this.upeCurrencyScale = this._sessionService.getContainerUpeCurrencyScale();
    this.pageSizes = PaginationConstants.PAGE_SIZES;
    this.getAllTransactions();
    this.getParameters();
    this.listenForErrorMessages();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  save(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.errors = [];
    if ((this.pendingSelectedTransactions.length === 0 && !this.parametersChanged) &&
      this.pendingTransactionsToRemove.length === 0) {
      this.closeAllModals();
      return;
    }

    const payload = this.getPayloadForSave();
    this._tbasService.saveControlled(this.data.tba.tbaId, payload).subscribe(() => {
      this.closeAllModals();
      this.success();
    });
  }

  // override
  close(): void {
    if (this.pendingSelectedTransactions.length > 0) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: 'Close Add Transactions',
          content: 'Are you sure you want to cancel your actions and close?',
        } as Message,
        success: () => {
          this.closeAllModals();
        },
      });
    } else {
      this.closeAllModals();
    }
  }

  selectedTransactionChange(transaction: TbaTransaction): void {
    this.errors = [];
    if (transaction.selected) {
      this.validateTransactionAdding(transaction);
      // handle transaction is already added to TBA and deselected at some point
      if (transaction.included) {
        const pendingToRemove = this.pendingTransactionsToRemove.find(pendingTransaction => pendingTransaction.transactionId === transaction.transactionId);
        if (!pendingToRemove) {
          return;
        }
        this.pendingTransactionsToRemove = this.pendingTransactionsToRemove.filter(pendingTransaction => pendingTransaction.transactionId !== transaction.transactionId);
        return;
      }
      // adding new transaction
      this.pendingSelectedTransactions.push(transaction);
    } else {
      // prepare to remove previously included transaction
      if (transaction.included) {
        this.pendingTransactionsToRemove.push(transaction);
        return;
      }
      // remove pending transaction
      const pendingTransactionToRemove = this.pendingSelectedTransactions.find(pendingTransaction => pendingTransaction.transactionId === transaction.transactionId);
      if (!pendingTransactionToRemove) {
        return;
      }
      this.pendingSelectedTransactions = this.pendingSelectedTransactions.filter(pendingSelectedTransaction => pendingSelectedTransaction.transactionId !== transaction.transactionId);
    }
  }

  getEntityCode(transaction: Transaction, isSource = true): string {
    return this._utilsService.getEntityCodeFromTransaction(transaction, isSource);
  }

  pageChanged(event: any): void {
    this.currentPage = event.page;
    this.getAllTransactions();
  }

  pageSizeChanged(): void {
    this.getAllTransactions();
  }

  onParameterValueChanged(event: any, element, field, parameter: ControlledParameter, tbaParameterDescription: string): void {
    const twoDecimalPrices = { [TbaConstants.TBA_PARAMETERS_COGS_RELATED]: true, [TbaConstants.TBA_PARAMETERS_COGS_UNRELATED]: true, [TbaConstants.TBA_PARAMETERS_RESALE_PRICE]: true, [TbaConstants.TBA_PARAMETERS_SALES_PRICE]: true };
    twoDecimalPrices[tbaParameterDescription] ? this.formatNumberInput(event, element, field, parameter, 2) : this.formatNumberInput(event, element, field, parameter, 4);
    this.errors = [];
    this.parametersChanged = true;
  }

  // setting temp value for formatted input field
  setOldValue(event, field) {
    this.inputOldValue[field] = event.target.value;
  }

  getFormattedNumber(value: number, maxFractionDigits = 1): string {
    return this._numberFormatService.getFormatedNumber(value, maxFractionDigits);
  }

  getAdditionalParamLabel(parameter: TbaParameter): string {
    if (Object.values(TbaParameterAmountEnum).includes(parameter.tbaParameterId)) {
      return `(${this.upeCurrency.isoCode}${this.getCurrencyScaleString()})`;
    }

    return '';
  }

  private getCurrencyScaleString(): string {
    return this._utilsService.getCurrencyScaleString(this.upeCurrencyScale);
  }

  private getPayloadForSave(): TbaTransactionsPayload {
    const payload = new TbaTransactionsPayload();
    payload.addTransactionIds =
      this.pendingSelectedTransactions ? this.pendingSelectedTransactions.map((transaction: TbaTransaction) => transaction.transactionId) : [];
    payload.removeTransactionIds =
      this.pendingTransactionsToRemove ? this.pendingTransactionsToRemove.map((transaction: TbaTransaction) => transaction.transactionId) : [];
    payload.tbaControlledParameters = this.parameters ? this.parameters.map((parameter: ControlledParameter) => {
      if (!parameter.parameterValue && parameter.tbaParameter.optional) {
        parameter.parameterValue = 0;
      }
      // since backend updates by clearing old ones first we don't want ids to be sent
      delete parameter.tbaControlledParameterId;
      return parameter;
    }) : [];

    return payload;
  }

  private isSameTradingPair(transactions: TbaTransaction[]): boolean {
    const firstLegalEntityId = transactions[0].legalEntityTransactions[0].entity.entityId;
    const secondLegalEntityId = transactions[0].legalEntityTransactions[1].entity.entityId;
    const sameTradingPair = transactions.every((transaction) => {
      if (!transaction.legalEntityTransactions) {
        return false;
      } else {
        return transaction.legalEntityTransactions.find(legalEntityTransaction => legalEntityTransaction.entity.entityId === firstLegalEntityId) !== undefined &&
        transaction.legalEntityTransactions.find(legalEntityTransaction => legalEntityTransaction.entity.entityId === secondLegalEntityId) !== undefined;
      }
    });
    const sourceLegalEntityTransaction = transactions[0].legalEntityTransactions ? transactions[0].legalEntityTransactions.find(legalEntityTransaction => legalEntityTransaction.isSource) : null;
    const destLegalEntityTransaction = transactions[0].legalEntityTransactions ? transactions[0].legalEntityTransactions.find(legalEntityTransaction => !legalEntityTransaction.isSource) : null;
    const sourceId = sourceLegalEntityTransaction ? sourceLegalEntityTransaction.entity.entityId : 0;
    const destinationId = destLegalEntityTransaction ? destLegalEntityTransaction.entity.entityId : 0;

    const sameDirection = transactions.every((transaction) => {
      if (!transaction.legalEntityTransactions) {
        return false;
      } else {
        return transaction.legalEntityTransactions.find(legalEntityTransaction => legalEntityTransaction.isSource).entity.entityId === sourceId &&
        transaction.legalEntityTransactions.find(legalEntityTransaction => !legalEntityTransaction.isSource).entity.entityId === destinationId;
      }
    });

    return sameTradingPair && sameDirection;
  }

  // validating here until we have backend validation endpoints implemented
  private validateTransactionAdding(transaction: TbaTransaction): void {
    const transactionsToCompare: TbaTransaction[] = [transaction];
    let isSameTradingPair: boolean;

    const includedTransactions = this.transactions.filter(tbaTransaction => {
      return tbaTransaction.selected && tbaTransaction.included && tbaTransaction.transactionId !== transaction.transactionId;
    });

    if (includedTransactions.length > 0 || this.pendingSelectedTransactions.length > 0) {
      transactionsToCompare.push(...includedTransactions);
      transactionsToCompare.push(...this.pendingSelectedTransactions);
    }
    isSameTradingPair = this.isSameTradingPair(transactionsToCompare);
    if (!isSameTradingPair) {
      this.errors.push('Cannot add transactions from a different trading pair or in different trading pair direction!');
    }

    if (!transaction.units &&
      this.data.tba.tbaEvaluationMethod.tbaEvaluationMethodId === TbaEvaluationMethodEnum.CUTCUP &&
      this.data.tba.transactionType.transactionTypeId === TransactionTypeEnum.TangibleGoods) {
      this.modalService.openModal({
        component: MessageModalComponent,
        data: {
          title: 'Error',
          content: 'This transaction cannot be added as it does not contain units!',
        } as Message,
        success: () => {
          transaction.selected = false;
          this.pendingSelectedTransactions = this.pendingSelectedTransactions.filter(pendingSelectedTransaction => pendingSelectedTransaction.transactionId !== transaction.transactionId);
        },
      });
    }
  }

  private getAllTransactions(): void {
    this._tbasService
      .listSuitableTransactions(this.data.tba.tbaId, `?${this.getPaginationQueryString()}`).subscribe((res) => {
        this.transactions = res.result.transactions ? res.result.transactions.map((transaction: TbaTransaction) => {
          const isAlreadyAdded = transaction.tbas ? !!transaction.tbas.find(tba => tba.tbaId === this.data.tba.tbaId) : false;
          transaction.selected =
            (isAlreadyAdded && !this.pendingTransactionsToRemove.find(pendingTransaction => transaction.transactionId === pendingTransaction.transactionId)) ||
            !!this.pendingSelectedTransactions.find(pendingTransaction => transaction.transactionId === pendingTransaction.transactionId);
          transaction.included = isAlreadyAdded;
          return transaction;
        }) : [];
        this.totalItems = res.result.count;
      });
  }

  private getParameters(): void {
    const parametersPayload = this.data.parameters ? this.data.parameters.map(parameter => parameter) : [];
    this._tbasService.getAllowedParameters(this.data.tba.tbaId).subscribe((res) => {
      this.parameters = res.result.parameters ? res.result.parameters.map((parameter: TbaParameter) => {
        const alreadyAdded: ControlledParameter = parametersPayload.find((param: ControlledParameter) => param.tbaParameter.tbaParameterId === parameter.tbaParameterId);
        let controlledParameter: ControlledParameter;
        if (!!alreadyAdded) {
          alreadyAdded.tbaParameter = parameter;
          return alreadyAdded;
        }

        controlledParameter = new ControlledParameter();
        controlledParameter.tbaParameter = parameter;
        controlledParameter.tba = new Tba();
        controlledParameter.tba.tbaId = this.data.tba.tbaId;
        return controlledParameter;
      }) : [];
    });
  }

  private getPaginationQueryString(): string {
    const queryParameters = [];
    let queryString: string;
    if (!!this.selectedPageSize) {
      queryParameters.push(`length=${this.selectedPageSize}`);
    }
    if (!!this.currentPage) {
      queryParameters.push(`page=${this.currentPage}`);
    }
    if (queryParameters.length > 0) {
      queryString = `${queryParameters.join('&')}`;
    }
    return queryString;
  }

  private listenForErrorMessages(): void {
    this.subscriptions.push(
      this._notificationsService.notification$.subscribe((message) => {
        this.handleErrors(message);
      })
    );
  }

  private handleErrors(res: HttpErrorResponse): void {
    this.isSaveDisabled = false;

    if (res.status === 400) {
      // this.errors.push(res.error.errors.global);
    } else if (res.status === 422) {
      const parameterErrors = Object.keys(res.error.errors).filter(key => {
        return key.includes('tbaControlledParameters');
      });
      if (parameterErrors.length > 0) {
        this.errors.push('One or more parameter values are not valid!');
      }
    } else {
      // this.errors.push(TranslationConstants.GLOBAL_SERVER_ERROR);
    }
  }

  private formatNumberInput(
    event: any,
    element: any, // HTML element passed for manipulating cursor location
    field: string,
    parameter: ControlledParameter,
    maxFractionDigits = 1): void {

    const validInputRegex = /^[-]?[0-9,]*[.]?[0-9]*$/g;
    const firstChar = event.target.value.slice(0, 1);
    const startsWithDotOrComma = firstChar.includes('.') || firstChar.includes(',');
    const startsWithMinus = firstChar === '-';

    if (!validInputRegex.test(event.target.value)) {
      event.target.value = this.inputOldValue[field];

      return;
    }

    parameter[field] = this._numberFormatService.handleCursorPosition(
      event,
      element,
      parameter[field],
      maxFractionDigits,
      startsWithDotOrComma,
      startsWithMinus
    );
  }
}
